/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { GraduationCap, BookOpen, Award, ArrowLeft, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { getEcmCatalog, getEcmArticle, submitEcmQuiz } from '@/lib/growth';

const EcmPage = () => {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [article, setArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const isPro = ['medici', 'infermieri', 'oss'].includes(user?.category);

  const load = useCallback(async () => {
    setLoading(true);
    try { setCatalog(await getEcmCatalog()); } catch { setCatalog(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isPro) load(); }, [isPro, load]);

  const open = async (id) => {
    setSelected(id); setArticle(null); setResult(null); setAnswers([]);
    setArticleLoading(true);
    try { setArticle(await getEcmArticle(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore caricamento'); setSelected(null); }
    finally { setArticleLoading(false); }
  };

  const submit = async () => {
    if (!article || answers.length !== (article.quiz || []).length) {
      toast.error('Rispondi a tutte le domande'); return;
    }
    try {
      const r = await submitEcmQuiz(selected, answers);
      setResult(r);
      if (r.passed && r.credits_awarded > 0) toast.success(`+${r.credits_awarded} crediti ECM`);
      else if (r.passed) toast.info('Quiz superato (crediti già assegnati)');
      else toast.error(`Non superato (${r.score}/${r.total})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  if (!isPro) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">ECM riservato ai professionisti sanitari.</CardContent></Card>;
  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`ecm-skel-${i}`} className="h-24 rounded-xl" />)}</div>;
  if (!catalog) return null;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="ecm-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-primary" /> Assistente ECM
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Leggi articoli sintetizzati dall'AI, supera il quiz, guadagna crediti ECM.</p>
        </div>
        <div className="rounded-xl border border-border p-3 flex items-center gap-2" data-testid="ecm-credits">
          <Award className="h-5 w-5 text-primary" />
          <div>
            <div className="text-lg font-bold tabular-nums">{catalog.total_credits}</div>
            <div className="text-[10px] text-muted-foreground uppercase">Crediti totali</div>
          </div>
        </div>
      </div>

      {!selected && (
        <div className="grid gap-2 sm:grid-cols-2">
          {catalog.articles.map((a, i) => (
            <motion.div key={a.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => open(a.id)} data-testid={`ecm-card-${a.id}`}>
                <CardContent className="p-4 space-y-1.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="secondary" className="text-[10px]">{a.specialty}</Badge>
                    <Badge variant="outline" className="text-[10px]">{a.duration_min} min</Badge>
                    {a.completed && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" /> completato</Badge>}
                  </div>
                  <div className="font-semibold text-sm line-clamp-2">{a.title}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <BookOpen className="h-3 w-3" /> {catalog.credits_per_article} crediti al superamento
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {selected && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setArticle(null); setResult(null); }} data-testid="ecm-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Torna al catalogo
          </Button>
          {articleLoading && <div className="text-sm text-muted-foreground flex items-center gap-2"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Preparazione articolo AI…</div>}
          {article && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="font-display">{article.title}</CardTitle>
                  <CardDescription>{article.specialty} · {article.duration_min} min</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="whitespace-pre-wrap leading-relaxed">{article.summary}</div>
                  {article.key_points?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Punti chiave</div>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {article.key_points.map((p, i) => <li key={`kp-${i}-${(p || '').slice(0, 20)}`}>{p}</li>)}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-display">Quiz di verifica</CardTitle>
                  <CardDescription>Serve almeno il 70% per superare.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(article.quiz || []).map((q, qi) => {
                    const detail = result?.details?.[qi];
                    return (
                      <div key={q.id || `q-${qi}-${(q.question || '').slice(0, 20)}`} className="space-y-1.5" data-testid={`ecm-q-${qi}`}>
                        <div className="font-medium text-sm">{qi + 1}. {q.question}</div>
                        <div className="grid gap-1.5">
                          {(q.options || []).map((opt, oi) => {
                            const picked = answers[qi] === oi;
                            const showState = !!detail;
                            const isCorrect = showState && oi === detail.correct;
                            const isWrongPick = showState && picked && !detail.ok;
                            return (
                              <button key={`${q.id || qi}-opt-${oi}-${(opt || '').slice(0, 12)}`} type="button"
                                onClick={() => !result && setAnswers((a) => { const c = [...a]; c[qi] = oi; return c; })}
                                disabled={!!result}
                                data-testid={`ecm-q-${qi}-opt-${oi}`}
                                className={`text-left text-sm rounded-md border p-2 transition-colors ${
                                  isCorrect ? 'border-emerald-500/60 bg-emerald-500/10' :
                                  isWrongPick ? 'border-red-500/60 bg-red-500/10' :
                                  picked ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                                }`}>
                                <div className="flex items-center gap-2">
                                  {showState && isCorrect && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                                  {showState && isWrongPick && <XCircle className="h-3.5 w-3.5 text-red-600" />}
                                  <span>{opt}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {detail && detail.rationale && (
                          <div className="text-[11px] text-muted-foreground italic">{detail.rationale}</div>
                        )}
                      </div>
                    );
                  })}
                  {!result ? (
                    <Button onClick={submit} disabled={answers.length !== (article.quiz || []).length} data-testid="ecm-submit">
                      Invia risposte
                    </Button>
                  ) : (
                    <div className="rounded-md border border-border p-3 flex items-center gap-3">
                      <Award className={`h-8 w-8 ${result.passed ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                      <div>
                        <div className="font-semibold">Punteggio: {result.score}/{result.total} {result.passed ? '· Superato ✓' : '· Non superato'}</div>
                        {result.credits_awarded > 0 && (
                          <div className="text-xs text-emerald-700">+{result.credits_awarded} crediti aggiunti</div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EcmPage;
